export type FileType =
  | 'link'
  | 'image'
  | 'txt'
  | 'doc'
  | 'xls'
  | 'pdf'
  | 'csv'
  | 'html'
  | 'json'
  | 'mp4'
  | 'mkv'
  | 'compressed'
  | 'unknown';

export type FormattedFile = {
  name: string;
  type: string;
  size: number;
  webkitRelativePath: string;
  fileType: FileType;
  file: File;
  imgPreviewURL?: string;
  uploadProgress?: number;
  controller?: AbortController;
  isCanceled?: boolean;
};

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB in bytes
const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

/**
 * Maps a mime type to a custom file type.
 *
 * @param {string} mimeType
 * @returns {CustomFileType}
 */
export const getFileType = (mimeType: string): FileType => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType === 'video/x-matroska') return 'mkv';
  if (mimeType === 'text/plain') return 'txt';
  if (mimeType === 'text/csv') return 'csv';
  if (mimeType === 'text/html') return 'html';
  if (mimeType === 'application/json') return 'json';
  if (mimeType === 'application/pdf') return 'pdf';
  if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  )
    return 'xls';
  if (
    mimeType === 'application/msword' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
    return 'doc';
  if (mimeType.startsWith('application/x-url')) return 'link';
  if (
    mimeType.includes('application/x-zip-compressed') ||
    mimeType.includes('application/x-compressed')
  )
    return 'compressed';
  return 'unknown';
};

/**
 * Formats a file into a FormattedFile object.
 *
 * @param file The file to be formatted.
 * @returns A FormattedFile object.
 */
export const formatFile = (file: File): FormattedFile => {
  return {
    name: file.name,
    type: file.type,
    size: file.size,
    webkitRelativePath: file.webkitRelativePath,
    fileType: getFileType(file.type),
    file,
    imgPreviewURL:
      getFileType(file.type) === 'image'
        ? URL.createObjectURL(file)
        : undefined,
  };
};

/**
 * Checks if a file is under the maximum size limit.
 *
 * @param file The file to be checked.
 * @param customFileSize The maximum size limit in megabytes. @default 1
 * @returns True if the file is under the maximum size limit, false otherwise.
 */
export const validateFileSize = (
  file: File,
  customFileSize: number = 1,
): boolean => {
  customFileSize *= MAX_FILE_SIZE;
  if (!file.type?.length) {
    return false;
  }
  const fileType = getFileType(file.type);

  return (
    file.size <=
    (fileType === 'mp4' || fileType === 'mkv' ? MAX_VIDEO_SIZE : customFileSize)
  );
};

/**
 * Checks if a file has a valid type.
 * Make sure user doesn't upload a folder.
 *
 * @param file The file to be checked.
 * @returns True if the file has a valid type, false otherwise.
 */
export const validateFileType = (file: File): boolean => !!file.type?.length;
